import {
  Checkbox,
  CheckboxGroup,
  CheckboxGroupProps,
  CheckboxProps,
} from "@heroui/checkbox";
import { Input, InputProps, Textarea, TextAreaProps } from "@heroui/input";
import { Radio, RadioGroup, RadioGroupProps } from "@heroui/radio";
import { Select, SelectItem, SelectProps } from "@heroui/select";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

interface FormInputProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  name: Path<T>;
  required?: boolean;
}

export function FormInput<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  id,
  name,
  required,
  ...props
}: FormInputProps<T> & InputProps) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `input-${name}`;
  const errorId = `${fieldId}-error`;

  // Handle Enter key to move focus to next field instead of submitting form
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission

      // Find the next focusable form field (inputs/textareas/selects only)
      const form = e.currentTarget.closest("form");

      if (form) {
        const focusableFields = form.querySelectorAll(
          "input:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly]), select:not([disabled])",
        );
        const currentIndex = Array.from(focusableFields).indexOf(
          e.currentTarget,
        );
        const nextElement = focusableFields[currentIndex + 1] as
          | HTMLElement
          | undefined;

        if (nextElement) {
          nextElement.focus();
        } else {
          // If there is no next input field, submit the form
          form.requestSubmit();
        }
      }
    }

    // Call original onKeyDown if provided
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Input
              variant="bordered"
              {...props}
              {...field}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={!!error}
              aria-label={ariaLabel}
              aria-required={required ? "true" : undefined}
              id={fieldId}
              isInvalid={!!error}
              isRequired={required}
              onKeyDown={handleKeyDown as any}
            />
            {error && (
              <div
                className="mt-1 text-sm text-danger"
                id={errorId}
                role="alert"
              >
                {error.message}
              </div>
            )}
          </>
        );
      }}
    />
  );
}

interface FormTextareaProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  name: Path<T>;
  required?: boolean;
}

export function FormTextarea<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  id,
  name,
  required,
  ...props
}: FormTextareaProps<T> & TextAreaProps) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `textarea-${name}`;
  const errorId = `${fieldId}-error`;

  // Note: Textarea allows normal Enter key behavior for line breaks
  // Enhanced focus management is handled by FormInput component

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Textarea
              variant="bordered"
              {...props}
              {...field}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={!!error}
              aria-label={ariaLabel}
              aria-required={required ? "true" : undefined}
              id={fieldId}
              isInvalid={!!error}
              isRequired={required}
            />
            {error && (
              <div
                className="mt-1 text-sm text-danger"
                id={errorId}
                role="alert"
              >
                {error.message}
              </div>
            )}
          </>
        );
      }}
    />
  );
}

interface FormSelectProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  name: Path<T>;
  required?: boolean;
  "data-testid"?: string;
}

export function FormSelect<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  "data-testid": dataTestId,
  id,
  name,
  options,
  required,
  ...props
}: FormSelectProps<T> &
  Omit<SelectProps, "children" | "items"> & {
    options: { label: string; value: string; disabled?: boolean }[];
  }) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `select-${name}`;
  const errorId = `${fieldId}-error`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Select
              variant="bordered"
              {...props}
              {...field}
              disallowEmptySelection
              aria-describedby={error ? errorId : undefined}
              aria-invalid={!!error}
              aria-label={ariaLabel}
              aria-required={required ? "true" : undefined}
              data-testid={dataTestId}
              id={fieldId}
              isInvalid={!!error}
              isRequired={required}
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] || "";

                field.onChange(value);
              }}
            >
              {options.map((option) => (
                <SelectItem
                  key={option.value}
                  data-testid={`${dataTestId ?? fieldId}-option-${option.value.toLowerCase().replace(/\s+/g, "-")}`}
                  isDisabled={option.disabled}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
            {error && (
              <div
                className="mt-1 text-sm text-danger"
                id={errorId}
                role="alert"
              >
                {error.message}
              </div>
            )}
          </>
        );
      }}
    />
  );
}

interface FormCheckboxProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  label?: string;
  name: Path<T>;
  description?: string;
}

export function FormCheckbox<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  description,
  id,
  label,
  name,
  ...props
}: FormCheckboxProps<T> & CheckboxProps) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `checkbox-${name}`;
  const errorId = `${fieldId}-error`;
  const descriptionId = description ? `${fieldId}-description` : undefined;

  // Determine which IDs to include in aria-describedby
  const getAriaDescribedBy = (error: any) => {
    const ids = [];

    if (error) ids.push(errorId);
    if (descriptionId) ids.push(descriptionId);

    return ids.length > 0 ? ids.join(" ") : undefined;
  };

  return (
    <div>
      <Controller
        control={control}
        name={name}
        render={({ field: { value, ...field }, fieldState: { error } }) => (
          <>
            <Checkbox
              isSelected={value}
              {...field}
              {...props}
              aria-describedby={getAriaDescribedBy(error)}
              aria-invalid={!!error}
              aria-label={ariaLabel ?? label}
              id={fieldId}
              isInvalid={!!error}
            >
              {label}
            </Checkbox>

            {description && (
              <div className="mt-1 text-sm text-default-500" id={descriptionId}>
                {description}
              </div>
            )}

            {error && (
              <div
                className="mt-1 text-sm text-danger"
                id={errorId}
                role="alert"
              >
                {error.message}
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}

interface FormRadioGroupProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  name: Path<T>;
  options: { label: string; value: string }[];
}

export function FormRadioGroup<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  id,
  name,
  options,
  ...props
}: FormRadioGroupProps<T> & RadioGroupProps) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `radio-group-${name}`;
  const errorId = `${fieldId}-error`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <RadioGroup
              {...props}
              aria-describedby={error ? errorId : undefined}
              aria-invalid={!!error}
              aria-label={ariaLabel}
              id={fieldId}
              isInvalid={!!error}
              value={field.value}
              onValueChange={field.onChange}
            >
              {options.map((option, index) => (
                <Radio
                  key={option.value}
                  id={`${fieldId}-option-${index}`}
                  value={option.value}
                >
                  {option.label}
                </Radio>
              ))}
            </RadioGroup>
            {error && (
              <div
                className="mt-1 text-sm text-danger"
                id={errorId}
                role="alert"
              >
                {error.message}
              </div>
            )}
          </>
        );
      }}
    />
  );
}

interface FormCheckboxGroupProps<T extends FieldValues> {
  "aria-label"?: string;
  control: Control<T>;
  id?: string;
  label?: string;
  name: Path<T>;
  description?: string;
  options: { label: string; value: string }[];
}

export function FormCheckboxGroup<T extends FieldValues>({
  "aria-label": ariaLabel,
  control,
  description,
  id,
  label,
  name,
  options,
  ...props
}: FormCheckboxGroupProps<T> & Omit<CheckboxGroupProps, "children">) {
  // Generate a unique ID for this field if not provided
  const fieldId = id ?? `checkbox-group-${name}`;
  const errorId = `${fieldId}-error`;
  const descriptionId = description ? `${fieldId}-description` : undefined;

  // Determine which IDs to include in aria-describedby
  const getAriaDescribedBy = (error: any) => {
    const ids = [];

    if (error) ids.push(errorId);
    if (descriptionId) ids.push(descriptionId);

    return ids.length > 0 ? ids.join(" ") : undefined;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div>
          <CheckboxGroup
            label={label}
            {...props}
            aria-describedby={getAriaDescribedBy(error)}
            aria-invalid={!!error}
            aria-label={ariaLabel}
            className="flex flex-col gap-2"
            id={fieldId}
            isInvalid={!!error}
            value={field.value}
            onValueChange={field.onChange}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {options.map((option, index) => (
                <Checkbox
                  key={option.value}
                  className="min-w-[120px]"
                  id={`${fieldId}-option-${index}`}
                  value={option.value}
                >
                  {option.label}
                </Checkbox>
              ))}
            </div>
          </CheckboxGroup>

          {description && (
            <div className="mt-1 text-sm text-default-500" id={descriptionId}>
              {description}
            </div>
          )}

          {error && (
            <div className="mt-1 text-sm text-danger" id={errorId} role="alert">
              {error.message}
            </div>
          )}
        </div>
      )}
    />
  );
}
